/**
 * La huella de este dispositivo.
 *
 * Es un identificador aleatorio y persistente, NO una huella derivada del
 * navegador (canvas, fuentes, user-agent). La razón es doble: el
 * fingerprinting pasivo es hostil con el usuario y además no funciona —
 * dos iPhones del mismo modelo con el mismo iOS producen la misma huella
 * derivada, y ahí el candado I2 rechazaría a desconocidos legítimos con
 * "este pase es para alguien más". Un aleatorio no colisiona nunca por
 * accidente.
 *
 * El servidor la usa para el candado I2 (ADR-012, spec §3.2): no se entra
 * con el pase de alguien desde su mismo teléfono. Es autoafirmada — quien
 * borre el almacenamiento tiene una nueva— y eso es deliberado: sube el
 * costo del abuso casual, no lo hace imposible.
 */

export const DEVICE_FP_KEY = 'gu.device_fp';

/** Suficiente para que dos huellas nunca choquen por azar. */
const BYTES = 16;

/**
 * Huella en memoria para cuando el almacenamiento no está disponible
 * (Safari en modo privado llegó a lanzar al escribir). Dura lo que la
 * pestaña, que es peor que persistir pero infinitamente mejor que dejar
 * a alguien sin poder entrar.
 */
let enMemoria: string | null = null;

function generar(): string {
  const bytes = new Uint8Array(BYTES);
  crypto.getRandomValues(bytes);
  // Hex: sin `|`, que es el separador de la cookie de invitación, y sin
  // los `+/=` de base64 que obligarían a escapar en cualquier log o URL.
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Devuelve la huella de este dispositivo, creándola la primera vez.
 *
 * Estable entre llamadas y entre recargas: de eso depende que el candado
 * I2 del servidor pueda reconocer dos entradas desde el mismo aparato.
 */
export function deviceFingerprint(): string {
  try {
    const guardada = localStorage.getItem(DEVICE_FP_KEY);
    // Una cadena en blanco o basura se descarta: valdría como huella y
    // haría colisionar a todo el que la tuviera.
    if (guardada && guardada.trim().length >= BYTES) return guardada;
  } catch {
    /* almacenamiento bloqueado: se resuelve abajo */
  }

  const nueva = enMemoria ?? generar();

  try {
    localStorage.setItem(DEVICE_FP_KEY, nueva);
  } catch {
    // No se pudo persistir. Al menos que sea estable dentro de la sesión:
    // el flujo de alta entero ocurre en una sola pestaña.
    enMemoria = nueva;
  }

  return nueva;
}
